import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, PiggyBank } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';

const MONTHS_IN_YEAR = 12;
const MAX_MONTHS = 600;

export const HomeLoanOverdraftCalculator = ({ showHeading = false }) => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(9);
  const [tenureYears, setTenureYears] = useState(20);
  const [surplusParked, setSurplusParked] = useState(500000);

  const [results, setResults] = useState({
    emi: 0,
    regularInterest: 0,
    odInterest: 0,
    interestSaved: 0,
    regularMonths: 0,
    odMonths: 0,
    monthsSaved: 0
  });

  const calculate = useCallback(() => {
    const monthlyRate = interestRate / MONTHS_IN_YEAR / 100;
    const totalMonths = tenureYears * MONTHS_IN_YEAR;
    const emi = (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
      (Math.pow(1 + monthlyRate, totalMonths) - 1);

    const regularInterest = emi * totalMonths - loanAmount;

    // Simulate OD account: interest charged only on (outstanding - surplus parked)
    let balance = loanAmount;
    let odInterest = 0;
    let odMonths = 0;
    while (balance > 0 && odMonths < MAX_MONTHS) {
      const effectivePrincipal = Math.max(balance - surplusParked, 0);
      const interestForMonth = effectivePrincipal * monthlyRate;
      const principalPaid = emi - interestForMonth;
      odInterest += interestForMonth;
      balance -= principalPaid;
      odMonths += 1;
    }

    setResults({
      emi,
      regularInterest,
      odInterest,
      interestSaved: Math.max(regularInterest - odInterest, 0),
      regularMonths: totalMonths,
      odMonths,
      monthsSaved: Math.max(totalMonths - odMonths, 0)
    });
  }, [loanAmount, interestRate, tenureYears, surplusParked]);

  useEffect(() => {
    calculate();
  }, [calculate]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatTenure = (months) => {
    const years = Math.floor(months / MONTHS_IN_YEAR);
    const rem = months % MONTHS_IN_YEAR;
    return rem > 0 ? `${years} yrs ${rem} mo` : `${years} yrs`;
  };

  return (
    <section className="py-20 bg-white" data-testid="home-loan-overdraft-calculator">
      <div className="container mx-auto px-4">
        {showHeading && (
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Home Loan Overdraft <span className="text-emerald-600">Calculator</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              See how much interest you can save by parking surplus funds in your home loan overdraft account
            </p>
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {/* Input Section */}
          <Card className="border-2 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-6 h-6 text-emerald-600" />
                Loan & Surplus Details
              </CardTitle>
              <CardDescription>Enter your home loan details and average surplus parked in the OD account</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Home Loan Amount</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(loanAmount)}</span>
                </div>
                <Slider
                  value={[loanAmount]}
                  onValueChange={(value) => setLoanAmount(value[0])}
                  min={500000}
                  max={50000000}
                  step={100000}
                  className="w-full"
                  data-testid="hlod-loan-amount-slider"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Interest Rate (p.a.)</Label>
                  <span className="text-xl font-bold text-emerald-600">{interestRate.toFixed(1)}%</span>
                </div>
                <Slider
                  value={[interestRate]}
                  onValueChange={(value) => setInterestRate(value[0])}
                  min={7}
                  max={15}
                  step={0.1}
                  className="w-full"
                  data-testid="hlod-interest-rate-slider"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Loan Tenure</Label>
                  <span className="text-xl font-bold text-emerald-600">{tenureYears} years</span>
                </div>
                <Slider
                  value={[tenureYears]}
                  onValueChange={(value) => setTenureYears(value[0])}
                  min={5}
                  max={30}
                  step={1}
                  className="w-full"
                  data-testid="hlod-tenure-slider"
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold text-slate-700">Avg. Surplus Parked in OD Account</Label>
                  <span className="text-xl font-bold text-emerald-600">{formatCurrency(surplusParked)}</span>
                </div>
                <Slider
                  value={[surplusParked]}
                  onValueChange={(value) => setSurplusParked(value[0])}
                  min={0}
                  max={Math.min(loanAmount, 20000000)}
                  step={50000}
                  className="w-full"
                  data-testid="hlod-surplus-slider"
                />
                <div className="text-sm text-slate-600">
                  Surplus funds reduce the interest-bearing principal every month
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          <div className="space-y-6">
            <Card className="border-2 border-emerald-500 bg-gradient-to-br from-emerald-50 to-white">
              <CardHeader>
                <CardTitle className="text-emerald-700">Your Savings with Home Loan Overdraft</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-white p-6 rounded-xl border-2 border-emerald-200 text-center">
                  <p className="text-sm text-slate-600 mb-2">Total Interest Saved</p>
                  <p className="text-4xl font-bold text-emerald-600" data-testid="hlod-interest-saved">
                    {formatCurrency(results.interestSaved)}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">compared to a regular home loan</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Monthly EMI</p>
                    <p className="text-xl font-bold text-slate-900" data-testid="hlod-emi">{formatCurrency(results.emi)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Tenure Reduced By</p>
                    <p className="text-xl font-bold text-slate-900" data-testid="hlod-tenure-saved">{formatTenure(results.monthsSaved)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Interest (Regular Loan)</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(results.regularInterest)}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-emerald-200">
                    <p className="text-sm text-slate-600 mb-1">Interest (With Overdraft)</p>
                    <p className="text-xl font-bold text-slate-900">{formatCurrency(results.odInterest)}</p>
                  </div>
                </div>

                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200 text-center">
                  <p className="text-sm text-slate-600">Loan Closes In</p>
                  <p className="text-2xl font-bold text-emerald-700" data-testid="hlod-new-tenure">{formatTenure(results.odMonths)}</p>
                  <p className="text-xs text-slate-500 mt-1">instead of {formatTenure(results.regularMonths)}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
              <CardContent className="pt-6">
                <PiggyBank className="w-8 h-8 mb-2" />
                <p className="text-sm opacity-90 mb-1">How it works</p>
                <p className="text-sm opacity-90">
                  In a home loan overdraft, your surplus funds parked in the linked
                  account reduce the interest-bearing principal. You pay interest only on the net
                  outstanding, saving lakhs in interest while keeping your funds accessible anytime.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
